import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Mobile, Desktop, IsMobileContent } from "../../media-queries/MediaQueries";
import LinkWrapper from "../../components/Global/LinkWrapper";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import Image from "../Global/Image/Image";
import { GLOBAL_PROPS } from "../Global/GlobalConstants";
import "./MaterialGuideCard.scss";
import { DISPLAYMODE } from "./SearchToolbar/DisplayMode";

function MaterialAttribute({ checked = false, label = "" }) {
  return (
    <>
      {checked && (
        <div
          className={`material-guide-card__body__material-attributes__item bold-medium ${
            checked ? "material-guide-card__body__material-attributes__item--checked bold-bold" : ""
          }`}
        >
          <span className="darticon-checkmark material-guide-card__body__material-attributes__item__checkmark" />
          {label}
        </div>
      )}
    </>
  );
}

function MaterialIcon({ name = "" }) {
  return (
    <li className="material-guide-card__body__material-type-icons__item">
      <span className={`dart${name}`} aria-label={name} title={name} role="img" />
    </li>
  );
}

function MaterialGuideCard({ materialType = {}, displayMode, externalModalDiv = null, positionModal }) {
    const {
      title = "",
      image = "",
      link = {},
      icons: materialIcons = [],
      doesAccessRecyclingExists = false,
      isMadeWithRenewableResources = false,
      isCommerciallyCompostable = false,
      hasRecycledContent = false,
      isSourceReduced = false,
    } = materialType;

    const isMobile = IsMobileContent();

    const [isHovered, setIsHovered] = useState(false);
    const [isModalHovered, setIsModalHovered] = useState(false);
    const cardRef = useRef(null);

    // Position modal at the top of the card on hover
    useEffect(() => {
      if (positionModal) {
        positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered);
        if (
          displayMode === DISPLAYMODE.GRIDCOMPACT &&
          externalModalDiv &&
          (isHovered || isModalHovered) &&
          cardRef.current
        ) {
          window.addEventListener("scroll", () => positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered), true);
          window.addEventListener("resize", () => positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered));

          return () => {
            window.removeEventListener("scroll", () => positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered), true);
            window.removeEventListener("resize", () => positionModal(cardRef, externalModalDiv, displayMode, isHovered, isModalHovered));
          };
        }
      }
    }, [isHovered, isModalHovered, displayMode, externalModalDiv]);

  try {
    const getLabel = (key) => {
      switch (key) {
        case "accessRecyclingExists":
          return window.globalProperties?.[GLOBAL_PROPS.MATERIAL_LISTING.ACCESS_RECYCLING_LABEL];
        case "commerciallyCompostable":
          return window.globalProperties?.[
            GLOBAL_PROPS.MATERIAL_LISTING.COMMERCIAL_COMPOSTING_LABEL
          ];
        case "containsRecycledContent":
          return window.globalProperties?.[GLOBAL_PROPS.MATERIAL_LISTING.CONTAINS_RECYCLABES_LABEL];
        case "madeWithRenewableResources":
          return window.globalProperties?.[GLOBAL_PROPS.MATERIAL_LISTING.RENEWABLE_RESOURCES_LABEL];
        case "sourceReduced":
          return window.globalProperties?.[GLOBAL_PROPS.MATERIAL_LISTING.SOURCE_REDUCED_LABEL];
        default:
          return "";
      }
    };

    const attributes = {
      containsRecycledContent: { checked: hasRecycledContent },
      accessRecyclingExists: { checked: doesAccessRecyclingExists },
      madeWithRenewableResources: {
        checked: isMadeWithRenewableResources,
      },
      commerciallyCompostable: { checked: isCommerciallyCompostable },
      sourceReduced: { checked: isSourceReduced },
    };

    const labeledAttributes = Object.entries(attributes).map(({ 0: key, 1: value }) => {
      value.label = getLabel(key);
      return value;
    });

    const showCta = link.externalLink || link.internalLink;
    const viewAllLabel = link["label"] ? link.label : window.globalProperties?.[GLOBAL_PROPS.MATERIAL_LISTING.VIEW_ALL_BUTTON_LABEL];

    const detailsContainer = () => {
      const content = (
        <div className="material-guide-card__body__content-wrapper">
            <div className="material-guide-card__body__content">
              
              {labeledAttributes.length > 0 && (
                <>
                  {labeledAttributes.map(({ checked, label }, index) => {
                    return (
                      <MaterialAttribute key={`${label}-${index}`} checked={checked} label={label} />
                    );
                  })}
                </>
              )}
            </div>
            <div className={`material-guide-card__body__material-type-icons__wrapper ${displayMode}`}>
              {materialIcons.length > 0 && (
                <ul className="material-guide-card__body__material-type-icons">
                  {materialIcons.map((name, index) => (
                    <MaterialIcon name={name} key={index} />
                  ))}
                </ul>
              )}
              {showCta && (
                <Desktop>
                  <LinkWrapper
                    className="material-guide-card__body__material-type-icons__wrapper__button btn btn--primary"
                    link={link}
                  >
                    {viewAllLabel}
                  </LinkWrapper>
                </Desktop>
              )}
            </div>
        </div>
      );
      return content;
    }

    const renderCardContent = (renderDetails = false) => (
      <div className={`material-guide-card__wrapper ${displayMode}`}>
        <div className="material-guide-card__media">
          <Image className="material-guide-card__media__image" src={image} alt="" loading="lazy" />
        </div>
        <div className="material-guide-card__body">
          <h2 className="material-guide-card__body__title bold-extra-bold">{title}</h2>
            {displayMode !== DISPLAYMODE.GRIDCOMPACT || renderDetails ? (
              detailsContainer()
            ) : ""}
          {showCta && (
            <Mobile>
              <LinkWrapper
                className="btn btn--primary material-guide-card__media__button"
                link={link}
              >
                {viewAllLabel}
              </LinkWrapper>
            </Mobile>
          )}
        </div>
      </div>
    );

    return (
      <div className="material-guide-card"
        ref={cardRef}
        {...(!isMobile && {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        })}
      >
        {renderCardContent()}
        {/* If GRIDCOMPACT, render the entire card into the modal portal */}
        {displayMode === DISPLAYMODE.GRIDCOMPACT && externalModalDiv && (isHovered || isModalHovered) && (
          ReactDOM.createPortal(
            <div className="product-and-product-family">
              {renderCardContent(true)}
            </div>,
            externalModalDiv
          )
        )}
      </div>
    );

  } catch (e) {
    return (
      <ErrorComponent
        error={e}
        componentName={MaterialGuideCard.displayName || MaterialGuideCard.name}
      />
    );
  }
}

export default MaterialGuideCard;
